import type { ReactNode } from 'react';
import { Badge, Button, Spinner } from '@monksflow/monks-ui';
import { ArrowRightIcon, PlusIcon, SparklesIcon, UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NEW_SEGMENT_CHIP } from './newSegmentChip';

interface SuggestedAudienceActionProps {
  saved: boolean;
  isSaving: boolean;
  /** The segment's share of market, placed before the button so the action stays last. */
  segmentShare?: ReactNode;
  onSave: () => void;
  onView?: () => void;
}

/**
 * Says where an audience stands and offers the one thing to do about it.
 *
 * Saved, there is nothing left to save, so the offer becomes a way to go and see it. That is
 * also what keeps the same audience from being saved twice: once the report records the
 * persona, the button that would create another is gone.
 */
export const SuggestedAudienceAction = ({
  saved,
  isSaving,
  segmentShare,
  onSave,
  onView,
}: SuggestedAudienceActionProps) => {
  const { t } = useTranslation('analysis');

  if (saved) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" roundness="full" className="dark:bg-surface-raised">
          <UsersIcon size={13} />
          {t('analysisResult.suggestedAudiences.audienceKnowledge')}
        </Badge>
        {segmentShare}
        {onView && (
          <Button variant="outline" size="mini" onClick={onView}>
            {t('analysisResult.suggestedAudiences.viewInKnowledgeHub')}
            <ArrowRightIcon size={15} />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge roundness="full" className={NEW_SEGMENT_CHIP}>
        <SparklesIcon size={13} />
        {t('analysisResult.suggestedAudiences.newSegment')}
      </Badge>
      {segmentShare}
      <Button variant="outline" size="mini" onClick={onSave} disabled={isSaving}>
        {isSaving
          ? t('analysisResult.suggestedAudiences.saving')
          : t('analysisResult.suggestedAudiences.saveToKnowledge')}
        {isSaving ? <Spinner size="sm" /> : <PlusIcon size={15} />}
      </Button>
    </div>
  );
};
